import { Box, Typography, FormControlLabel, Checkbox, Stack, InputAdornment, Alert, Tooltip, Link } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMessage, faPrint } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { Formik, Form, FormikHelpers } from "formik"
import * as Yup from "yup"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { UserToggle } from "./UserToggle"
import { FormikSubmitButton, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { checkInGuest } from "../../serviceCenterHomeApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { CompanySelect } from "../../../../../../shared/components/createOrder/orderOnBehalf/CompanySelect"
import { UserType } from "../../../../../login/loginTypes"
import { useEffect, useState } from "react"
import {
    defaultUserDetailsState,
    UserDetailsProps,
} from "../../../../../../shared/components/createOrder/orderOnBehalf/OrderOnBehalfFlow"
import { UserSelector } from "../../../../../../shared/components/createOrder/orderOnBehalf/UserSelector"
import { useLabelPrinter } from "../../hooks/useLabelPrinter"
import { GroupType, SingleType } from "../../serviceCenterHomeTypes"
import { ApiError } from "../../../../../../core/api/errorTypes"
import { isEmpty } from "lodash-es"
import { enqueueSnackbar } from "notistack"
import { LabelPrinterExtensionDialog } from "../../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"

const useStyles = makeStyles()((theme) => ({
    modal: {
        padding: "3rem",
    },
    guestDetails: {
        width: "65%",
    },
    settingsSection: {
        width: "35%",
    },
    checkinButton: {
        padding: ".5rem 3rem !important",
    },
}))

type FormValues = {
    userType: "single" | "group"
    guestName: string
    guestCompany: string
    numberOfGuests: string
    printGuestCard: boolean
    sendNotification: boolean
    messageToHost: string
}

export function GuestCheckInModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { classes } = useStyles()

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="service-center-reports-dashboard-check-in-modal-title">
                    <>Guest check-in</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="md"
            scroll="body"
        >
            <Box className={classes.modal}>
                <CheckInForm
                    variant="modal"
                    handleFinish={() => {
                        onClose()
                    }}
                />
            </Box>
        </StandardDialog>
    )
}

export function CheckInForm({ handleFinish, variant }: { handleFinish?: () => void; variant?: "modal" | "block" }) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const [userDetailsState, setUserDetailsState] = useState<UserDetailsProps>(defaultUserDetailsState)
    const [alerts, setAlerts] = useState<
        Array<{
            id: string
            type: "success" | "error"
            message: string
        }>
    >([])

    const { mutateAsync } = useApiMutation(checkInGuest, {
        async onSuccess(_params, data) {
            const successMessage =
                typeof data === "object" && "serviceResponse" in data && data?.serviceResponse?.displayMessage
                    ? data?.serviceResponse.displayMessage
                    : l10n.getString("service-center-reports-check-success-title")

            addAlert("success", successMessage)
        },
        async onError(_, error) {
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined
            if (displayMessage) {
                addAlert("error", displayMessage)
            } else if (error && typeof error === "object" && "displayMessage" in error) {
                enqueueSnackbar({
                    variant: "error",
                    message: error?.displayMessage as string,
                })
                return
            }
        },
    })

    const { printGuestLabel, printGroupLabels, hasError, hasSuccess, printErrorMessage, isInstalledError, clearState } =
        useLabelPrinter()

    useEffect(() => {
        // Close the modal if check-in is successful and label printing is done
        if (hasSuccess) {
            handleSuccess()
        }
    }, [hasSuccess])

    useEffect(() => {
        if (hasError && printErrorMessage) {
            addAlert("error", printErrorMessage)
        }
    }, [hasError, printErrorMessage])

    const addAlert = (type: "success" | "error", message: string) => {
        const id = Date.now().toString()
        setAlerts((prevAlerts) => [...prevAlerts, { id, type, message }])
    }

    const removeAlert = (id: string) => {
        setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
    }

    const removeAlertByMessage = (message: string) => {
        setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.message !== message))
    }

    const handleSuccess = () => {
        clearState()
        handleFinish?.()
    }

    const validationSchema = Yup.object().shape({
        userType: Yup.string()
            .oneOf(["single", "group"], "Invalid user type")
            .required(l10n.getString("required-field")),
        guestName: Yup.string().when("userType", {
            is: "single",
            then: (schema) =>
                schema.required(l10n.getString("required-field")).min(3, l10n.getString("required-field")),
            otherwise: (schema) => schema.notRequired(),
        }),
        // companyId: Yup.number().required(l10n.getString("required-field")),
        // selectedUserId: Yup.number().required(l10n.getString("required-field")),
        numberOfGuests: Yup.number().when("userType", {
            is: "group",
            then: (schema) => schema.required(l10n.getString("required-field")),
            otherwise: (schema) => schema.notRequired(),
        }),
        guestCompany: Yup.string().required(l10n.getString("required-field")),
        printGuestCard: Yup.boolean(),
        sendNotification: Yup.boolean(),
        messageToHost: Yup.string().max(500, "Message must be no more than 500 characters"),
    })

    const initialValues: FormValues = {
        userType: "single",
        guestName: "",
        numberOfGuests: "",
        guestCompany: "",
        printGuestCard: true,
        sendNotification: true,
        messageToHost: "",
    }

    async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
        try {
            let checkIns: SingleType[] | GroupType[] = []

            if (values.userType === "single") {
                checkIns = [
                    {
                        type: "SINGLE",
                        host: {
                            company: {
                                id: userDetailsState?.companyId ? +userDetailsState?.companyId : 0,
                            },
                            user: {
                                id: userDetailsState?.selectedUserId ?? 0,
                            },
                        },
                        guest: {
                            company: values.guestCompany,
                            name: values.guestName,
                        },
                    },
                ]
            }
            if (values.userType === "group") {
                checkIns = [
                    {
                        type: "GROUP",
                        host: {
                            company: {
                                id: userDetailsState?.companyId ? +userDetailsState?.companyId : 0,
                            },
                            user: {
                                id: userDetailsState?.selectedUserId ?? 0,
                            },
                        },
                        group: {
                            company: values.guestCompany,
                            count: +values.numberOfGuests,
                        },
                    },
                ]
            }

            if (!userDetailsState?.selectedUserId || !userDetailsState?.companyId) {
                addAlert("error", l10n.getString("service-center-reports-check-validation-error"))
                return
            }

            await mutateAsync({
                serviceCenterId: serviceCenterId ?? 0,
                checkIns,
                notifyHost: values.sendNotification,
                messageToHost: values.messageToHost,
            })
            if (values.printGuestCard) {
                if (values.userType === "single") {
                    await printGuestLabel({
                        visitorName: values.guestName,
                        visitorCompany: values.guestCompany,
                        hostName: userDetailsState?.selectedUserName ?? "",
                    })
                }
                if (values.userType === "group") {
                    await printGroupLabels({
                        groupName: values.guestCompany,
                        groupSize: +values.numberOfGuests,
                        hostName: userDetailsState?.selectedUserName ?? "",
                    })
                }
            }

            helpers.resetForm()
            setUserDetailsState(defaultUserDetailsState)
        } catch (error) {
            console.error("Check-in failed:", error)
        }
    }

    return (
        <>
            <LabelPrinterExtensionDialog open={isInstalledError} onClose={clearState} />
            <Formik<FormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <StandardDialog open={isSubmitting} hideCloseIcon onClose={clearState}>
                            <Stack alignItems="center" gap={3}>
                                <FontAwesomeIcon icon={faPrint} size="3x" />
                                <LocalizedStrict id="service-center-reports-check-in-modal-guest-details-printer-dialog-printing">
                                    <Typography>Printing label...</Typography>
                                </LocalizedStrict>
                            </Stack>
                        </StandardDialog>
                        {!isEmpty(alerts) && (
                            <Stack marginBottom="2rem" spacing="1rem">
                                {alerts.map((alert) => (
                                    <Alert
                                        key={alert.id}
                                        onClose={() => removeAlert(alert.id)}
                                        severity={alert.type}
                                        variant="filled"
                                    >
                                        {alert.message}
                                    </Alert>
                                ))}
                            </Stack>
                        )}
                        <Stack flexDirection="row" justifyContent="space-between" spacing="2rem" marginBottom="2rem">
                            <Box className={classes.guestDetails}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-guest-details">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Guest details
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <Stack spacing="1.5rem" marginBottom="3rem">
                                    <CompanySelect
                                        userDetails={userDetailsState}
                                        labelTranslationKey="create-order-on-behalf-of-dialog-select-company-host-field"
                                        setUserDetails={(data) => {
                                            if (typeof data === "function") {
                                                const newData = data(userDetailsState)
                                                setFieldValue("companyId", newData?.companyId)
                                            }
                                            setUserDetailsState(data)
                                        }}
                                        id={serviceCenterId ?? 0}
                                        userType={UserType.SERVICE_CENTER}
                                    />

                                    <UserSelector
                                        labelTranslationKey="create-order-on-behalf-of-dialog-select-host-field"
                                        userDetails={userDetailsState}
                                        setUserDetails={(value) => {
                                            setUserDetailsState(value)
                                            removeAlertByMessage(
                                                l10n.getString("service-center-reports-check-validation-error"),
                                            )
                                        }}
                                        id={serviceCenterId ?? 0}
                                        userType={UserType.SERVICE_CENTER}
                                    />
                                </Stack>
                                <UserToggle
                                    value={values.userType}
                                    onChange={(value) => setFieldValue("userType", value)}
                                />
                                {values.userType === "single" ? (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-guest-name"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField<FormValues>
                                            name="guestName"
                                            label="Guest name"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-number-of-guests"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField<FormValues>
                                            name="numberOfGuests"
                                            type="number"
                                            label="Number of guests"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id={
                                        values.userType === "single"
                                            ? "service-center-reports-check-in-modal-guest-company"
                                            : "service-center-reports-check-in-modal-guests-company"
                                    }
                                    attrs={{ label: true }}
                                >
                                    <FormikTextField<FormValues>
                                        name="guestCompany"
                                        label="Guest company"
                                        fullWidth
                                        margin="normal"
                                    />
                                </LocalizedStrict>
                            </Box>
                            <Box className={classes.settingsSection}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-settings">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Settings
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id="service-center-reports-check-in-modal-print-guest-card"
                                    attrs={{ label: true }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.printGuestCard}
                                                onChange={(e) => setFieldValue("printGuestCard", e.target.checked)}
                                            />
                                        }
                                        label="Print guest card"
                                    />
                                </LocalizedStrict>
                                <Tooltip
                                    title="Host will receive notification that the guest has been checked in."
                                    arrow
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="service-center-reports-check-in-modal-send-host-notification"
                                            attrs={{ label: true }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <LocalizedStrict
                                                        attrs={{ title: true }}
                                                        id="service-center-reports-check-in-modal-send-host-notification-tooltip"
                                                    >
                                                        <Checkbox
                                                            checked={values.sendNotification}
                                                            onChange={(e) =>
                                                                setFieldValue("sendNotification", e.target.checked)
                                                            }
                                                        />
                                                    </LocalizedStrict>
                                                }
                                                label="Notify host"
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                                {variant === "modal" && (
                                    <Box display="flex" alignItems="center" marginTop={2}>
                                        <LocalizedStrict
                                            id="service-center-reports-check-in-modal-write-message-to-host"
                                            attrs={{ label: true }}
                                        >
                                            <FormikTextField<FormValues>
                                                name="messageToHost"
                                                label="Write a message to host"
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <FontAwesomeIcon icon={faMessage} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                )}
                            </Box>
                        </Stack>

                        <Stack justifyContent="center" alignItems="center">
                            <LocalizedStrict id="service-center-reports-check-in-modal-check-in-button">
                                <FormikSubmitButton fullWidth={false} className={classes.checkinButton} type="submit">
                                    Check-in
                                </FormikSubmitButton>
                            </LocalizedStrict>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </>
    )
}
